/**
 * UranIA PA — Admin tenant detail (US-028 AC#2+AC#4+AC#5+AC#6).
 *
 * Route `/admin/tenants/$tenantId` — pagina di dettaglio + azione
 * `disable` / `enable`. La pagina e' la conferma esplicita richiesta da
 * US-F1-05 "permette create/disable con conferma esplicita".
 *
 * AC coperti dal componente:
 *   - AC#2 — Form di disabilitazione con campo `motivo` obbligatorio +
 *     conferma esplicita (Bootstrap Italia 2.x).
 *   - AC#3 — Solo `platform_admin` (RBAC guard come in list).
 *   - AC#4 — L'azione disable chiama `POST /api/v1/tenants/{id}:disable`.
 *     Step-up MFA: se il server risponde 403 con codice
 *     `step_up_required` + `acr_required` (US-013), il client mostra un
 *     alert con istruzioni di re-login (US-022+ implementera' il
 *     redirect automatico). La UI **non** invoca direttamente il
 *     livello acr — l'enforcement e' server-side.
 *   - AC#5 — axe-core serious = 0 (verificato in
 *     `spa-admin-tenants-axe.spec.ts`).
 *   - AC#6 — Typecheck strict.
 *
 * Boundary:
 *   - L'azione `enable` (riattivazione) non e' nell'AC ma e' fornita
 *     simmetricamente per ergonomia admin; l'endpoint server-side
 *     riusa lo stesso step-up guard.
 *   - Validazione lato client `motivo` non vuoto e' UX; il backend
 *     applica la propria validation con Problem+JSON.
 */

import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  disableTenant,
  enableTenant,
  getTenant,
  TenantsApiError,
  updateTenant,
} from '../../../lib/admin/tenants-api.ts';
import type {
  TenantDataClassification,
  TenantDetailDto,
  UpdateTenantBody,
} from '../../../lib/admin/tenants-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { TenantUsersSection } from '../../../components/admin/TenantUsersSection.tsx';
import { Route as RootRoute } from '../../__root.tsx';
import { ADMIN_TENANTS_QUERY_KEY } from './index.tsx';

function tenantDetailQueryKey(tenantId: string): readonly [string, string, string] {
  return ['admin', 'tenant-detail', tenantId];
}

interface DetailViewProps {
  readonly tenant: TenantDetailDto;
}

function TenantMeta(props: DetailViewProps): JSX.Element {
  const { t } = useTranslation();
  const { tenant } = props;
  return (
    <div className="card shadow-sm border-0 mb-3" data-testid="admin-tenant-detail-meta">
      <div className="card-header bg-white border-bottom py-2 px-3">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('admin.tenants.detail.meta_heading')}
        </span>
      </div>
      <div className="card-body">
        <dl className="row mb-0">
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.tenant_id')}</dt>
          <dd className="col-sm-8">
            <code className="small">{tenant.tenant_id}</code>
          </dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.codice_adg')}</dt>
          <dd className="col-sm-8">
            <code>{tenant.codice_adg}</code>
          </dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.nome')}</dt>
          <dd className="col-sm-8">{tenant.nome}</dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.regione')}</dt>
          <dd className="col-sm-8">{tenant.regione}</dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.dpo_email')}</dt>
          <dd className="col-sm-8">
            <a href={`mailto:${tenant.dpo_email}`}>{tenant.dpo_email}</a>
          </dd>
          <dt className="col-sm-4">
            {t('admin.tenants.detail.fields.data_classification_default')}
          </dt>
          <dd className="col-sm-8">
            <code>{tenant.data_classification_default}</code>
          </dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.status')}</dt>
          <dd className="col-sm-8">
            <span
              className={`badge ${tenant.status === 'active' ? 'bg-success' : 'bg-secondary'}`}
            >
              {t(`admin.tenants.status.${tenant.status}`)}
            </span>
          </dd>
          <dt className="col-sm-4">
            {t('admin.tenants.detail.fields.qdrant_collections')}
          </dt>
          <dd className="col-sm-8 small">
            <code>{tenant.qdrant_collections[0]}</code>
            <br />
            <code>{tenant.qdrant_collections[1]}</code>
          </dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.keycloak_group')}</dt>
          <dd className="col-sm-8">
            <code>{tenant.keycloak_group}</code>
          </dd>
          <dt className="col-sm-4">{t('admin.tenants.detail.fields.created_at')}</dt>
          <dd className="col-sm-8">
            <time dateTime={tenant.created_at}>{tenant.created_at}</time>
          </dd>
          {tenant.disabled_at !== undefined && tenant.disabled_at !== null && (
            <>
              <dt className="col-sm-4">{t('admin.tenants.detail.fields.disabled_at')}</dt>
              <dd className="col-sm-8">
                <time dateTime={tenant.disabled_at}>{tenant.disabled_at}</time>
              </dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}

const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/u;

interface EditFormProps {
  readonly tenant: TenantDetailDto;
  readonly onSaved: () => void;
  readonly onClose: () => void;
}

function EditTenantForm(props: EditFormProps): JSX.Element {
  const { t } = useTranslation();
  const nomeId = useId();
  const regioneId = useId();
  const dpoId = useId();
  const classId = useId();

  const [nome, setNome] = useState(props.tenant.nome);
  const [regione, setRegione] = useState(props.tenant.regione);
  const [dpoEmail, setDpoEmail] = useState(props.tenant.dpo_email);
  const [classification, setClassification] = useState<TenantDataClassification>(
    props.tenant.data_classification_default,
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const qc = useQueryClient();
  const mutation = useMutation<TenantDetailDto, TenantsApiError, UpdateTenantBody>({
    mutationFn: (body) => updateTenant(props.tenant.tenant_id, body),
    onSuccess: async (next) => {
      qc.setQueryData(['admin', 'tenant-detail', props.tenant.tenant_id], next);
      await qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
      props.onSaved();
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setValidationError(null);
    if (!EMAIL_PATTERN.test(dpoEmail.trim())) {
      setValidationError(t('admin.tenants.create.errors.dpo_email_invalid'));
      return;
    }
    if (nome.trim().length === 0 || regione.trim().length === 0) {
      setValidationError(t('admin.tenants.create.errors.required'));
      return;
    }
    mutation.mutate({
      nome: nome.trim(),
      regione: regione.trim(),
      dpo_email: dpoEmail.trim(),
      data_classification_default: classification,
    });
  };

  return (
    <div className="card shadow-sm border-0 mb-3" data-testid="admin-tenant-edit-card">
      <div className="card-header bg-white border-bottom py-2 px-3">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('admin.tenants.detail.edit.heading')}
        </span>
      </div>
      <div className="card-body">
        <form onSubmit={onSubmit} data-testid="admin-tenant-edit-form">
          <div className="row g-3">
            <div className="col-md-8">
              <label htmlFor={nomeId} className="form-label small text-muted mb-1">
                {t('admin.tenants.create.fields.nome')}
              </label>
              <input
                id={nomeId}
                type="text"
                className="form-control form-control-sm"
                value={nome}
                onChange={(e): void => setNome(e.target.value)}
                required
                maxLength={255}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor={regioneId} className="form-label small text-muted mb-1">
                {t('admin.tenants.create.fields.regione')}
              </label>
              <input
                id={regioneId}
                type="text"
                className="form-control form-control-sm"
                value={regione}
                onChange={(e): void => setRegione(e.target.value)}
                required
                maxLength={120}
              />
            </div>
            <div className="col-md-8">
              <label htmlFor={dpoId} className="form-label small text-muted mb-1">
                {t('admin.tenants.create.fields.dpo_email')}
              </label>
              <input
                id={dpoId}
                type="email"
                className="form-control form-control-sm"
                value={dpoEmail}
                onChange={(e): void => setDpoEmail(e.target.value)}
                required
              />
            </div>
            <div className="col-md-4">
              <label htmlFor={classId} className="form-label small text-muted mb-1">
                {t('admin.tenants.create.fields.data_classification_default')}
              </label>
              <select
                id={classId}
                className="form-select form-select-sm"
                value={classification}
                onChange={(e): void =>
                  setClassification(e.target.value as TenantDataClassification)
                }
              >
                <option value="public">public</option>
                <option value="internal">internal</option>
                <option value="restricted">restricted</option>
              </select>
            </div>
          </div>
          <p className="small text-muted mt-3 mb-0">
            {t('admin.tenants.detail.edit.codice_immutable_hint', {
              codice: props.tenant.codice_adg,
            })}
          </p>
          <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top gap-2">
            <div className="flex-grow-1">
              {validationError !== null && (
                <div className="text-danger small" role="alert">
                  {validationError}
                </div>
              )}
              {mutation.isError && (
                <div className="text-danger small" role="alert">
                  {mutation.error.detail ?? mutation.error.message}
                </div>
              )}
              {mutation.isSuccess && (
                <div className="text-success small" role="status">
                  {t('admin.tenants.detail.edit.saved')}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={props.onClose}
            >
              {t('admin.tenants.detail.edit.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={mutation.isPending}
              data-testid="admin-tenant-edit-submit"
            >
              {mutation.isPending
                ? t('admin.tenants.detail.edit.saving')
                : t('admin.tenants.detail.edit.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DisableFormProps {
  readonly tenant: TenantDetailDto;
  readonly onDisable: (motivo: string) => Promise<void>;
  readonly submitting: boolean;
  readonly error: TenantsApiError | null;
}

function DisableForm(props: DisableFormProps): JSX.Element {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const trimmed = motivo.trim();
  const error = props.error;
  const isStepUpError =
    error !== null &&
    (error.status === 403 ||
      error.code === 'step_up_required' ||
      (error.acrRequired !== null && error.acrRequired.length > 0));
  const motivoRequired = trimmed.length === 0;

  return (
    <form
      className="card shadow-sm"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (motivoRequired || !confirmed) {
          return;
        }
        void props.onDisable(trimmed);
      }}
      data-testid="admin-tenant-disable-form"
    >
      <div className="card-body">
        <h2 className="h4 mb-3">{t('admin.tenants.detail.disable.title')}</h2>
        <p className="text-muted">{t('admin.tenants.detail.disable.intro')}</p>
        <div className="form-group">
          <label htmlFor="admin-tenant-motivo" className="form-label">
            {t('admin.tenants.detail.disable.motivo_label')}
          </label>
          <textarea
            id="admin-tenant-motivo"
            className="form-control"
            rows={3}
            value={motivo}
            onChange={(event) => {
              setMotivo(event.target.value);
            }}
            aria-invalid={motivoRequired}
            aria-describedby="admin-tenant-motivo-hint"
            data-testid="admin-tenant-disable-motivo"
          />
          <small id="admin-tenant-motivo-hint" className="form-text text-muted">
            {t('admin.tenants.detail.disable.motivo_hint')}
          </small>
        </div>
        <div className="form-check mb-3">
          <input
            id="admin-tenant-confirm"
            type="checkbox"
            className="form-check-input"
            checked={confirmed}
            onChange={(event) => {
              setConfirmed(event.target.checked);
            }}
            data-testid="admin-tenant-disable-confirm"
          />
          <label htmlFor="admin-tenant-confirm" className="form-check-label">
            {t('admin.tenants.detail.disable.confirm_label', {
              codice: props.tenant.codice_adg,
            })}
          </label>
        </div>

        {error !== null && isStepUpError && (
          <div
            className="alert alert-warning"
            role="alert"
            data-testid="admin-tenant-disable-stepup-required"
          >
            <strong>{t('admin.tenants.detail.errors.step_up_title')}</strong>
            <p className="mb-0">
              {t('admin.tenants.detail.errors.step_up_body', {
                acr: (error.acrRequired ?? []).join(', ') || 'L2',
              })}
            </p>
          </div>
        )}
        {error !== null && !isStepUpError && (
          <div
            className="alert alert-danger"
            role="alert"
            data-testid="admin-tenant-disable-error"
          >
            <strong>{error.message}</strong>
            {error.detail !== null && <p className="mb-0">{error.detail}</p>}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-danger"
          disabled={props.submitting || motivoRequired || !confirmed}
          data-testid="admin-tenant-disable-submit"
        >
          {props.submitting
            ? t('admin.tenants.detail.disable.submitting')
            : t('admin.tenants.detail.disable.submit')}
        </button>
      </div>
    </form>
  );
}

interface EnableFormProps {
  readonly tenant: TenantDetailDto;
  readonly onEnable: () => Promise<void>;
  readonly submitting: boolean;
  readonly error: TenantsApiError | null;
}

function EnableForm(props: EnableFormProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <form
      className="card shadow-sm"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void props.onEnable();
      }}
      data-testid="admin-tenant-enable-form"
    >
      <div className="card-body">
        <h2 className="h4 mb-3">{t('admin.tenants.detail.enable.title')}</h2>
        <p className="text-muted">
          {t('admin.tenants.detail.enable.intro', { codice: props.tenant.codice_adg })}
        </p>
        {props.error !== null && (
          <div
            className="alert alert-danger"
            role="alert"
            data-testid="admin-tenant-enable-error"
          >
            <strong>{props.error.message}</strong>
            {props.error.detail !== null && <p className="mb-0">{props.error.detail}</p>}
          </div>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={props.submitting}
          data-testid="admin-tenant-enable-submit"
        >
          {props.submitting
            ? t('admin.tenants.detail.enable.submitting')
            : t('admin.tenants.detail.enable.submit')}
        </button>
      </div>
    </form>
  );
}

export function AdminTenantDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { tenantId } = useParams({ from: '/admin/tenants/$tenantId' });
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const query = useQuery<TenantDetailDto, TenantsApiError>({
    queryKey: tenantDetailQueryKey(tenantId),
    queryFn: () => getTenant(tenantId),
    enabled: isAuthenticated && isPlatformAdmin && tenantId.length > 0,
    staleTime: 30_000,
  });

  const invalidate = useCallback(
    async (next: TenantDetailDto): Promise<void> => {
      queryClient.setQueryData(tenantDetailQueryKey(tenantId), next);
      await queryClient.invalidateQueries({ queryKey: ADMIN_TENANTS_QUERY_KEY });
    },
    [queryClient, tenantId],
  );

  const disableMutation = useMutation<TenantDetailDto, TenantsApiError, string>({
    mutationFn: (motivo) => disableTenant(tenantId, { motivo }),
    onSuccess: (next) => {
      void invalidate(next);
    },
  });

  const enableMutation = useMutation<TenantDetailDto, TenantsApiError>({
    mutationFn: () => enableTenant(tenantId),
    onSuccess: (next) => {
      void invalidate(next);
    },
  });

  if (!isAuthenticated) {
    return (
      <section
        className="urania-admin-tenant-detail"
        data-testid="admin-tenant-detail-unauthenticated"
      >
        <h1 className="display-5 mb-3">{t('admin.tenants.detail.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('admin.tenants.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('admin.tenants.unauthenticated.message')}</p>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="admin-tenant-detail-go-to-login"
          >
            {t('admin.tenants.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section
        className="urania-admin-tenant-detail"
        data-testid="admin-tenant-detail-forbidden"
      >
        <h1 className="display-5 mb-3">{t('admin.tenants.detail.title')}</h1>
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="admin-tenant-detail-forbidden-alert"
        >
          <strong>{t('admin.tenants.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.tenants.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-tenant-detail" data-testid="admin-tenant-detail-page">
      <nav aria-label={t('admin.tenants.detail.breadcrumb_aria_label')} className="mb-3">
        <Link to="/admin/tenants" data-testid="admin-tenant-detail-back">
          {t('admin.tenants.detail.back_to_list')}
        </Link>
      </nav>
      <h1 className="display-5 mb-3">{t('admin.tenants.detail.title')}</h1>

      {query.isLoading && (
        <p
          role="status"
          aria-live="polite"
          data-testid="admin-tenant-detail-loading"
        >
          {t('admin.tenants.detail.loading')}
        </p>
      )}

      {query.isError && (
        <div
          className="alert alert-danger"
          role="alert"
          data-testid="admin-tenant-detail-error"
        >
          <strong>{t('admin.tenants.detail.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {query.isSuccess && (
        <>
          <div className="d-flex justify-content-end mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={(): void => setEditing((v) => !v)}
              data-testid="admin-tenant-edit-toggle"
            >
              {editing
                ? t('admin.tenants.detail.edit.close')
                : t('admin.tenants.detail.edit.open')}
            </button>
          </div>
          {editing ? (
            <EditTenantForm
              tenant={query.data}
              onSaved={(): void => {
                window.setTimeout(() => setEditing(false), 800);
              }}
              onClose={(): void => setEditing(false)}
            />
          ) : (
            <TenantMeta tenant={query.data} />
          )}
          <TenantUsersSection tenantId={query.data.tenant_id} />
          {query.data.status === 'active' ? (
            <DisableForm
              tenant={query.data}
              onDisable={async (motivo) => {
                await disableMutation.mutateAsync(motivo);
              }}
              submitting={disableMutation.isPending}
              error={disableMutation.error ?? null}
            />
          ) : (
            <EnableForm
              tenant={query.data}
              onEnable={async () => {
                await enableMutation.mutateAsync();
              }}
              submitting={enableMutation.isPending}
              error={enableMutation.error ?? null}
            />
          )}
        </>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/tenants/$tenantId',
  component: AdminTenantDetailPage,
});
